import { useCallback, useEffect, useRef, useState } from 'react';

import { faQuestionCircle } from '@fortawesome/pro-duotone-svg-icons';
import { MuiTheme } from 'config/materialUI';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ORANGE_SECONDARY } from 'constants/colors';

import { rSize } from 'components/utils';

import { getEmbeddedDashboardConfig } from 'RestAPIs/embedded-dashboard';
import {
  createAccountTrial,
  getAccountTrialStatus,
  TrialType
} from 'RestAPIs/trials';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { ThemeProvider } from '@mui/material/styles';

import { FontAwesomeSvgIcon } from 'components/shared';

import EmbeddedDashboardComparisonDialog from './EmbeddedDashboardComparisonDialog';
import EmbeddedDashboardPromoDialog from './EmbeddedDashboardPromoDialog';

const EmbeddedDashboardStyles = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .iframe-container {
    flex-grow: 1;
  }

  iframe {
    border: 0;
  }

  .user-voice-btn {
    position: fixed;
    right: ${rSize(30)};
    bottom: ${rSize(30)};

    svg {
      background-color: ${ORANGE_SECONDARY};
      fill: white;
      border-radius: 50%;
      padding: 2px;

      path:first-child {
        display: none;
      }
    }
  }
`;

const TrialInfoStyles = styled.div`
  padding: 0;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: auto;
  margin-right: 150px;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: center;

  .promo-bar {
    padding: 5px;
    font-weight: bold;
    font-size: 1.4em;

    button {
      margin-left: 20px;
    }
  }
`;

const loadPowerBiClient = (domNode) =>
  new Promise((resolve) => {
    window.powerbi.reset(domNode);
    const { models } = window['powerbi-client'];
    window.powerbi.bootstrap(domNode, { type: 'report' });

    getEmbeddedDashboardConfig().then(({ data }) => {
      const reportLoadConfig = {
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken: data.embedToken.token,
        id: data.embedReport.id,
        // Use other embed report config based on the requirement. We have used the first one for demo purpose
        embedUrl: data.embedReport.embedUrl,
        permissions: models.Permissions.All,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: false,
          background: models.BackgroundType.Transparent
        }
      };

      // Embed Power BI report when Access token and Embed URL are available
      const powerBiReport = window.powerbi.embed(domNode, reportLoadConfig);

      // Clear any other error handler event
      powerBiReport.off('error');
      // Below patch of code is for handling errors that occur during embedding
      // powerBiReport.on('error', (event) => {
      //     const errorMsg = event.detail;

      //     // Use errorMsg variable to log error in any destination of choice
      //     console.error(errorMsg);
      // });

      powerBiReport.off('buttonClicked');

      resolve(powerBiReport);
    });
  });

const loadUserVoiceScript = () =>
  new Promise((resolve) => {
    const userVoiceScript = document.createElement('script');
    userVoiceScript.type = 'text/javascript';
    userVoiceScript.src =
      '//sdb.uservoice.com/widget_environment/aANyNMSJRKddTj3T5dwew.js';
    const firstPageScript = document.getElementsByTagName('script')[0];
    firstPageScript.parentNode?.insertBefore(userVoiceScript, firstPageScript);
    userVoiceScript.onload = () => {
      resolve(true);
    };
  });

type Props = {
  tabsPromoNode: HTMLDivElement;
};

export default function EmbeddedDashboard({ tabsPromoNode }: Props) {
  const { t } = useTranslation('common');
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [isAllowedToTrial, setIsAllowedToTrial] = useState<boolean | null>(
    null
  );
  const [numberOfTrialDaysLeft, setNumberOfTrialDaysLeft] = useState(0);
  const iframeContainerRef = useRef(document.createElement('div'));
  const tabsPromoRoot = createRoot(tabsPromoNode);

  const addPowerBiBtnHandler = (powerBiReport) => {
    powerBiReport.on('buttonClicked', (event) => {
      if (
        event &&
        event.detail &&
        event.detail.title &&
        (event.detail.title === 'BtnLOCKED_AT' ||
          event.detail.title === 'BtnLOCKED_VL' ||
          event.detail.title === 'BtnLOCKED_VS')
      ) {
        setShowPromoDialog(true);
      }
    });
  };

  const fetchData = useCallback(() => {
    getAccountTrialStatus(TrialType.EMBEDDED_ANALYTICS).then(({ data }) => {
      let isAllowedToTrial = false;

      if (data) {
        setIsTrialActive(data.isStarted && data.numberOfDaysLeft != null);
        setNumberOfTrialDaysLeft(data.numberOfDaysLeft);
        isAllowedToTrial = !data.isStarted && data.numberOfDaysLeft != null;
        setIsAllowedToTrial(isAllowedToTrial);
      }

      loadPowerBiClient(iframeContainerRef.current).then((powerBiReport) => {
        if (isAllowedToTrial) {
          addPowerBiBtnHandler(powerBiReport);
        }
      });
    });
  }, []);

  const startTrial = () => {
    setShowPromoDialog(false);
    createAccountTrial(TrialType.EMBEDDED_ANALYTICS).then(() => {
      fetchData();
    });
  };

  const moreInfoBtnHanlder = useCallback(() => {
    if (isTrialActive) {
      setShowComparisonDialog(true);
    } else {
      setShowPromoDialog(true);
    }
  }, [isTrialActive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isTrialActive || isAllowedToTrial) {
      tabsPromoRoot.render(
        <ThemeProvider theme={MuiTheme}>
          <TrialInfoStyles>
            <div className="promo-bar d-flex v-center">
              <div>
                {isTrialActive
                  ? t('embeddedDashboard.trial.activeTrial_interval', {
                      postProcess: 'interval',
                      count: numberOfTrialDaysLeft
                    })
                  : t('embeddedDashboard.trial.tryForFreeText')}
              </div>
              <Button
                onClick={moreInfoBtnHanlder}
                variant="contained"
                size="small"
              >
                {t('moreInfo')}
              </Button>
            </div>
          </TrialInfoStyles>
        </ThemeProvider>
      );
    }

    return () => {
      tabsPromoRoot.render(<></>);
    };
  }, [
    isTrialActive,
    isAllowedToTrial,
    tabsPromoNode,
    t,
    numberOfTrialDaysLeft,
    moreInfoBtnHanlder
  ]);

  useEffect(() => {
    if (!window.UserVoice) {
      loadUserVoiceScript().then(() => {
        window.UserVoice.push([
          'set',
          {
            accent_color: ORANGE_SECONDARY,
            trigger_color: 'white',
            trigger_background_color: ORANGE_SECONDARY
          }
        ]);
      });
    }
  }, []);

  return (
    <EmbeddedDashboardStyles>
      <div ref={iframeContainerRef} className="iframe-container" />
      {showPromoDialog && (
        <EmbeddedDashboardPromoDialog
          onHidden={() => setShowPromoDialog(false)}
          onAction={startTrial}
        />
      )}
      {showComparisonDialog && (
        <EmbeddedDashboardComparisonDialog
          isTrialActive={isTrialActive}
          numberOfTrialDaysLeft={numberOfTrialDaysLeft}
          onHidden={() => setShowComparisonDialog(false)}
          onBuyAdvanced={fetchData}
        />
      )}
      <IconButton
        onClick={() => window.UserVoice.show()}
        className="user-voice-btn"
        color="primary"
      >
        <FontAwesomeSvgIcon icon={faQuestionCircle} fontSize="large" />
      </IconButton>
    </EmbeddedDashboardStyles>
  );
}
